import { Number } from 'effect';

export type MaxOrderable = number | null;

export const getMaxOrderable = (maxOrderable: MaxOrderable | undefined) =>
  maxOrderable === undefined ? 0 : maxOrderable;

export const getMaxOrderableInf = (maxOrderable: MaxOrderable) => maxOrderable ?? Infinity;

export const getAvailableQuantityWithCartInMind = (
  maxOrderable: MaxOrderable,
  quantityInCart: number
) => {
  if (maxOrderable === null) {
    return Infinity;
  }

  return Number.max(0, maxOrderable - quantityInCart);
};

export const canDecrementQuantity = (quantityInCart: number) => {
  return quantityInCart > 1;
};

export const canIncrementQuantity = (maxOrderable: MaxOrderable, quantityInCart: number) => {
  return getAvailableQuantityWithCartInMind(maxOrderable, quantityInCart) > 0;
};

export const checkAvailable = (available: boolean | undefined) => available ?? false;

const shouldAddToCartButtonBeDisabledHelper = (availableQuantity: number, currentAmount: number) =>
  availableQuantity === 0 || currentAmount === 0 || currentAmount > availableQuantity;

/**
 * null means unlimited stock (Infinity) - availableQuantity will never be 0
 * 0 means out of stock - different error state, not "exceeded", so we exclude it
 * Only consider exceeded when maxOrderable > 0 and no more available
 */
export const isMaxOrderableExceededInCart = (
  maxOrderable: MaxOrderable,
  quantityInCart: number
): boolean => {
  const availableQuantity = getAvailableQuantityWithCartInMind(maxOrderable, quantityInCart);

  return maxOrderable !== null && maxOrderable > 0 && availableQuantity === 0;
};

/**
 * Calculate if add-to-cart button should be disabled based on stock and cart state
 *
 * Logic:
 * 1. If maxOrderable is 0 (out of stock) -> always disabled
 * 2. If max orderable exceeded in cart (all units already in cart) -> enabled to show error message
 * 3. Otherwise -> check if requested amount is valid against available quantity
 *
 * @param maxOrderable - Maximum orderable quantity (null = unlimited)
 * @param quantityInCart - Current quantity already in cart
 * @param requestedAmount - Amount user wants to add (must be explicit)
 * @param price - Price of the product (duh)
 * @returns true if button should be disabled
 */
export const shouldAddToCartButtonBeDisabled = (
  maxOrderable: MaxOrderable,
  quantityInCart: number,
  requestedAmount: number,
  price: number
): boolean => {
  if (price <= 0) {
    return true;
  }

  const availableQuantity = getAvailableQuantityWithCartInMind(maxOrderable, quantityInCart);
  const shouldShowMaxOrderableErrorState = isMaxOrderableExceededInCart(
    maxOrderable,
    quantityInCart
  );

  return maxOrderable === 0
    ? true
    : shouldShowMaxOrderableErrorState
      ? false
      : shouldAddToCartButtonBeDisabledHelper(availableQuantity, requestedAmount);
};
